<template>
  <ShowcasesFormSuccess
    v-if="isSubmitted"
    @submit-another-game="onSubmitAnotherGame"
  />
  <template v-else>
    <div v-if="!isLoaded" class="py-10 flex justify-center">
      <BaseSpinner />
    </div>
    <ShowcasesFormClosed
      v-else-if="
        showcase?.requests_availability !==
        ShowcaseRequestsAvailability.Available
      "
    />
    <div v-else>
      <ShowcasesFormBanner class="hidden md:block mb-7" />
      <div
        class="hidden md:block text-2xl text-gray-900 font-bold mb-4 px-4 sm:px-0"
      >
        {{ $t('showcases.showcase_application') }}
      </div>
      <div ref="formsContainer" class="flex flex-col gap-6">
        <ShowcasesFormInfo ref="infoComponent" @submit-request="submit" />

        <BaseCard v-if="!authStore.profile">
          <div
            class="py-8 flex flex-col space-y-4 justify-center items-center text-center"
          >
            <div class="sm:whitespace-pre-line">
              {{ $t('showcases.login_to_see_showcase_form') }}
            </div>
            <BaseButton
              theme="primary"
              look="border"
              @click="authStore.openLoginModal"
            >
              {{ $t('header.login') }}
            </BaseButton>
          </div>
        </BaseCard>

        <template v-else>
          <ShowcasesProfileForm
            ref="profileFormComponent"
            :external-processing="isProcessing"
            @loading="isProfileFormLoading = $event"
            @submit-request="submit"
          />
          <ShowcasesCompanyForm
            ref="companyFormComponent"
            :external-processing="isProcessing"
            @loading="isCompanyFormLoading = $event"
            @company-change="onCompanyChange"
            @submit-request="submit"
          />
          <ShowcasesGameForm
            ref="gameFormComponent"
            :initial-game="initialGame"
            :external-processing="isProcessing"
            @loading="isGameFormLoading = $event"
            @submit-request="submit"
          />
          <ShowcasesAdditionalQuestionsForm
            ref="additionalQuestionsFormComponent"
            :disabled="isProcessing"
            @submit-request="submit"
          />
          <BaseCard>
            <i18n-t
              class="text-sm text-gray-500"
              keypath="showcases.gamefinder_featured"
              tag="div"
            >
              <template #link>
                <BaseLink :href="GAMEFINDER_LINK" target="_blank">
                  WN Game Finder
                </BaseLink>
              </template>
            </i18n-t>
          </BaseCard>
          <div
            class="flex flex-col-reverse xs:flex-row flex-wrap justify-between gap-y-4 mr-4 sm:mr-0"
          >
            <BaseLocaleLink :to="eventLink" class="text-center">
              <BaseButton look="link">
                <BaseIcon name="outline_chevron_left" class="mr-2" />
                {{ $t(`events.back_to_event`) }}
              </BaseButton>
            </BaseLocaleLink>
            <BaseButton
              class="ml-5"
              :loading="isProcessing"
              :disabled="commonLoading"
              @click="submit"
              >{{ $t('common.submit') }}
            </BaseButton>
          </div>
        </template>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import ShowcasesAdditionalQuestionsForm from '~/components/showcases/form/ShowcasesAdditionalQuestionsForm.vue'
import ShowcasesCompanyForm from '~/components/showcases/form/ShowcasesCompanyForm.vue'
import ShowcasesGameForm from '~/components/showcases/form/ShowcasesGameForm.vue'
import ShowcasesProfileForm from '~/components/showcases/form/ShowcasesProfileForm.vue'
import { EventType } from '~/models/events'
import { ShowcaseRequestsAvailability } from '~/models/showcases'
import { useEvents } from '~/stores/events'
import { useGames } from '~/stores/games'
import { useNotifications } from '~/stores/notifications'
import { useShowcaseRequests } from '~/stores/showcase-requests'
import { useShowcases } from '~/stores/showcases'
import { useAuth } from '~/stores/auth'
import type ShowcasesFormInfo from '~/components/showcases/form/ShowcasesFormInfo.vue'
import type { GameLite } from '~/models/games'

const { t } = useI18n()
const route = useRoute()
const { pushNotification } = useNotifications()
const gamesStore = useGames()
const showcaseRequestsStore = useShowcaseRequests()
const showcasesStore = useShowcases()
const eventsStore = useEvents()
const authStore = useAuth()
const { showcase } = storeToRefs(showcasesStore)
const { event } = storeToRefs(eventsStore)

const GAMEFINDER_LINK = 'https://wnhub.io/event-info/gamefinder'

const infoComponent = ref<InstanceType<typeof ShowcasesFormInfo> | null>(null)
const formsContainer = ref<InstanceType<typeof HTMLDivElement> | null>(null)

const profileFormComponent = ref<InstanceType<
  typeof ShowcasesProfileForm
> | null>(null)

const companyFormComponent = ref<InstanceType<
  typeof ShowcasesCompanyForm
> | null>(null)

const gameFormComponent = ref<InstanceType<typeof ShowcasesGameForm> | null>(
  null
)

const additionalQuestionsFormComponent = ref<InstanceType<
  typeof ShowcasesAdditionalQuestionsForm
> | null>(null)

const eventLink = computed(() =>
  event.value
    ? eventsStore.getEventPageRoute(event.value.slug, EventType.Default)
    : ''
)

const isProfileFormLoading = ref<boolean>(false)
const isCompanyFormLoading = ref<boolean>(false)
const isGameFormLoading = ref<boolean>(false)

const isProcessing = ref<boolean>(false)
const isSubmitted = ref<boolean>(false)

const initialGame = ref<GameLite | null>(null)

const commonLoading = computed(
  () =>
    isProfileFormLoading.value ||
    isCompanyFormLoading.value ||
    isGameFormLoading.value
)

const isLoaded = computed(() => {
  return (
    event.value &&
    event.value.slug === route.params.event &&
    showcase.value &&
    showcase.value.event_id === event.value.id &&
    showcase.value.slug === route.params.slug
  )
})

onServerPrefetch(async () => {
  await fetchShowcase()
})

onBeforeMount(async () => {
  await fetchShowcase()
})

watch(
  () => authStore.profile,
  async () => {
    await fetchCompanyGames(authStore.profile?.company_id)
  }
)

async function fetchShowcase() {
  await eventsStore.fetchEvent(route.params.event as string, !!authStore.auth)

  if (event.value?.id) {
    const showcasePromise = showcasesStore.fetchShowcase(
      event.value.id,
      route.params.slug as string
    )

    const gamesPromise = fetchCompanyGames(authStore.profile?.company_id)

    await Promise.all([showcasePromise, gamesPromise])
  }

  if (
    !showcase.value ||
    showcase.value.requests_availability ===
      ShowcaseRequestsAvailability.Forbidden
  ) {
    throw useCreateScreenError({ message: 'showcase not found' })
  }
}

// used in ShowcasesGameForm
async function fetchCompanyGames(companyId?: number | null): Promise<void> {
  companyId
    ? await gamesStore.fetchCompanyGames(companyId)
    : gamesStore.resetCompanyGames()
}

async function onCompanyChange(companyId: number | null): Promise<void> {
  await fetchCompanyGames(companyId)
}

async function submit(): Promise<void> {
  if (
    !authStore.profile ||
    !infoComponent.value ||
    !profileFormComponent.value ||
    !companyFormComponent.value ||
    !gameFormComponent.value ||
    !additionalQuestionsFormComponent.value
  ) {
    return
  }

  const showcaseId = showcase.value?.id

  if (!showcaseId || commonLoading.value || isProcessing.value) {
    return
  }

  isProcessing.value = true

  const validations = [
    await infoComponent.value.validateParticipation(),
    await profileFormComponent.value.validate(),
    await companyFormComponent.value.validate(),
    await gameFormComponent.value.validate(),
    await additionalQuestionsFormComponent.value.validate(),
  ]

  if (validations.includes(false)) {
    isProcessing.value = false

    await nextTick(() => {})
    formsContainer.value?.querySelector('.has-error')?.scrollIntoView?.()

    pushNotification({
      title: t(`notifications.system.check_fields`),
      theme: 'info',
      icon: 'outline_exclamation_triangle',
    })

    return
  }

  let profileUpdated = false

  try {
    await profileFormComponent.value.submit()

    profileUpdated = true

    await companyFormComponent.value.submit()
    await gameFormComponent.value.submit()

    const gameId = gamesStore.game?.id

    if (!gameId) {
      throw new Error('Invalid game')
    }

    const participationData = infoComponent.value.getParticipationData()

    await showcaseRequestsStore.createShowcaseRequest({
      showcase_id: showcaseId,
      game_id: gameId,
      is_on_site: participationData?.isOnSite || false,
      is_contest_participant: participationData?.isContestParticipant || false,
      additional_questions_answers:
        additionalQuestionsFormComponent.value?.getAnswers(),
    })

    pushNotification({
      title: t(`notifications.profile.update_success`),
    })

    isSubmitted.value = true
  } catch (error) {
    pushNotification({
      title: t(
        `notifications.showcases.${
          profileUpdated ? 'create_error_but_profile_updated' : 'create_error'
        }`
      ),
      theme: 'destructive',
    })
    useLogError(error)
  } finally {
    isProcessing.value = false
  }
}

function onSubmitAnotherGame(game: GameLite | null) {
  initialGame.value = game
  isSubmitted.value = false
}
</script>
